import { useSettlementStore } from "./store";
import {
  Checkbox,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Tooltip,
} from "@gw/web-basic-components";
import React, { cloneElement, ComponentProps, useEffect, useMemo } from "react";
import ColorPicker from "./color-picker";
import BorderPicker from "./border-picker";
import useOperate from "./hooks/use-operate";
import DotLabel from "../dot-label";
import { DATA_TYPE, VALUE_TYPE } from "./contanst";
import ValueEditor from "./value-editor";
import { Path, TableEditorProps } from "./dto";
import {
  getFormulaListField,
  hasFieldType,
  isField,
  isVaildFormila,
} from "./utils";
import useFlattenFields from "./hooks/use-flatten-fields";
import useTools from "./hooks/use-tools";
import { set } from "lodash";

import { ReactComponent as DeleteColumnIcon } from "./assets/delete-column.svg";
import { ReactComponent as DeleteRowIcon } from "./assets/delete-row.svg";
import { ReactComponent as InsertRowAbove } from "./assets/insert-row-above.svg";
import { ReactComponent as InsertRowBelow } from "./assets/insert-row-below.svg";
import { ReactComponent as InsertColumnLeft } from "./assets/insert-row-left.svg";
import { ReactComponent as InsertColumnRight } from "./assets/insert-row-right.svg";
import { ReactComponent as MergeIcon } from "./assets/merge-cells.svg";
import { ReactComponent as SplitIcon } from "./assets/solit-cells.svg";

const operations = [
  {
    label: "拆分单元格",
    icon: <SplitIcon />,
    key: "split",
    type: "single",
  },
  {
    label: "合并单元格",
    icon: <MergeIcon />,
    key: "merge",
    type: "multiple",
  },
  {
    label: "向上插入一行",
    icon: <InsertRowAbove />,
    key: "insert-row-above",
    type: "single",
  },
  {
    label: "向下插入一行",
    icon: <InsertRowBelow />,
    key: "insert-row-below",
    type: "single",
  },
  {
    label: "向左插入一列",
    icon: <InsertColumnLeft />,
    key: "insert-column-left",
    type: "single",
  },
  {
    label: "向右插入一列",
    icon: <InsertColumnRight />,
    key: "insert-column-right",
    type: "single",
  },
  {
    label: "删除列",
    icon: <DeleteColumnIcon />,
    key: "delete-columns",
  },
  {
    label: "删除行",
    icon: <DeleteRowIcon />,
    key: "delete-rows",
  },
];

const Panel = ({ fields }: Pick<TableEditorProps, "fields">) => {
  const [{ selection, mode, config }] = useSettlementStore();

  const {
    getSelectionConfig,
    setSelectionConfig,
    splitCell,
    mergeCell,
    insertRow,
    insertCol,
    deleteCols,
    deleteRows,
  } = useOperate();

  const [form] = Form.useForm();
  // 收集历史记录
  const collect = useTools();
  // 原始字段
  const [atomFlds, atomFldCodes] = useFlattenFields(fields);
  // 原始字段+公式字段
  const withFormulasFields = useMemo(() => {
    const formulas = config.formulas.filter((f) => f.custom);
    if (formulas.length === 0) return fields;
    return [
      ...fields,
      {
        name: "自定义参数",
        code: "custom",
        isList: false,
        type: VALUE_TYPE.NUMBER,
        children: formulas.map((i) => {
          // 是否有list字段
          const listField = getFormulaListField(i.value, atomFlds);
          const valid = isVaildFormila(i, atomFldCodes);
          return {
            ...i,
            children: [],
            isList: !!listField,
            type: VALUE_TYPE.NUMBER,
            formula: [],
            parentCode: listField?.parentCode ?? "custom",
            disabled: !valid,
            tooltip: valid ? undefined : "公式中存在不存在的字段",
          };
        }),
        formula: [],
      },
    ];
  }, [JSON.stringify(config.formulas), JSON.stringify(fields)]);

  const [flds] = useFlattenFields(withFormulasFields);

  useEffect(() => {
    form.resetFields();
    const selectionConfig = getSelectionConfig();
    form.setFieldsValue(selectionConfig);
  }, [JSON.stringify(selection)]);

  if (!selection.rect) return null;

  const setFieldValue = (path: Path, value: unknown) => {
    collect();
    form.setFieldValue(path, value);
    const cell = {};
    set(cell, path, value);
    setSelectionConfig(cell);
  };

  const renderImageSizeInput = (type: "width" | "height") => (
    <InputNumber
      min={0}
      step={5}
      precision={0}
      addonBefore={type === "width" ? "宽" : "高"}
      addonAfter={
        <div
          role="button"
          className="wbuc-cursor-pointer"
          onClick={() => {
            setFieldValue(["attr", type], "auto");
          }}
        >
          自适应
        </div>
      }
    />
  );

  return (
    <div className="wbuc-flex wbuc-w-[22rem] wbuc-flex-shrink-0 wbuc-flex-col wbuc-overflow-auto wbuc-rounded-[0.5rem] wbuc-bg-[#FBFBFB] wbuc-p-5">
      <h3>功能区</h3>
      <div className="wbuc-flex wbuc-gap-2 wbuc-bg-[#FFFFFF] wbuc-p-2">
        {operations
          .filter(({ type }) => mode === type || !type)
          .map(({ key, label, icon }) => (
            <Tooltip key={key} title={label}>
              {cloneElement<ComponentProps<"svg">>(icon, {
                className:
                  "wbuc-size-6 wbuc-cursor-pointer wbuc-text-[#707070]",
                onClick: () => {
                  switch (key) {
                    case "split":
                      collect();
                      splitCell();
                      break;
                    case "merge":
                      collect();
                      mergeCell();
                      break;
                    case "insert-row-above":
                      collect();
                      insertRow("before");
                      break;
                    case "insert-row-below":
                      collect();
                      insertRow("after");
                      break;
                    case "insert-column-left":
                      collect();
                      insertCol("before");
                      break;
                    case "insert-column-right":
                      collect();
                      insertCol("after");
                      break;
                    case "delete-columns":
                      collect();
                      deleteCols();
                      break;
                    case "delete-rows":
                      collect();
                      deleteRows();
                      break;
                    default:
                      break;
                  }
                },
              })}
            </Tooltip>
          ))}
      </div>
      <Form
        layout="vertical"
        className="wbuc-flex wbuc-flex-col wbuc-gap-5 wbuc-py-5"
        form={form}
        onValuesChange={(changed) => {
          collect();
          setSelectionConfig(changed);
        }}
      >
        <Form.Item
          label="自动合并"
          className="wbuc-mb-0"
          name="autoMerge"
          initialValue={false}
        >
          <Select
            options={[
              {
                label: "是",
                value: true,
              },
              {
                label: "否",
                value: false,
              },
            ]}
          />
        </Form.Item>
        <Form.Item
          label="自动换行"
          className="wbuc-mb-0"
          name={["style", "textWrap"]}
          initialValue="nowrap"
        >
          <Select
            options={[
              {
                label: "是",
                value: "wrap",
              },
              {
                label: "否",
                value: "nowrap",
              },
            ]}
          />
        </Form.Item>
        <Form.Item
          label="尺寸类型"
          className="wbuc-mb-0"
          name="sizeMode"
          initialValue="auto"
        >
          <Select
            options={[
              {
                label: "自适应",
                value: "auto",
              },
              {
                label: "固定",
                value: "manual",
              },
            ]}
          />
        </Form.Item>
        <div className="wbuc-flex wbuc-gap-2">
          <Form.Item
            className="wbuc-mb-0 wbuc-flex-1"
            label="字体大小"
            name={["style", "fontSize"]}
            initialValue={14}
          >
            <Select
              options={Array.from({ length: 17 }, (_, i) => ({
                label: 12 + i,
                value: 12 + i,
              }))}
            />
          </Form.Item>
          <Form.Item
            className="wbuc-mb-0 wbuc-flex-1"
            label="是否加粗"
            name={["style", "fontWeight"]}
            initialValue="normal"
          >
            <Select
              options={[
                {
                  label: "是",
                  value: "bold",
                },
                {
                  label: "否",
                  value: "normal",
                },
              ]}
            />
          </Form.Item>
        </div>
        <div className="wbuc-flex wbuc-gap-2">
          <Form.Item
            className="wbuc-mb-0 wbuc-flex-1"
            label="字体颜色"
            name={["style", "color"]}
            getValueFromEvent={(e) => (e === "none" ? undefined : e)}
          >
            <ColorPicker />
          </Form.Item>
          <Form.Item
            className="wbuc-mb-0 wbuc-flex-1"
            label="背景颜色"
            name={["style", "backgroundColor"]}
            getValueFromEvent={(e) => (e === "none" ? undefined : e)}
          >
            <ColorPicker />
          </Form.Item>
          <Form.Item
            className="wbuc-mb-0 wbuc-flex-1"
            label="边框样式"
            name={["style", "border"]}
          >
            <BorderPicker />
          </Form.Item>
        </div>
        <div className="wbuc-flex wbuc-gap-2">
          <Form.Item
            className="wbuc-mb-0 wbuc-flex-1"
            label="水平对齐"
            name={["style", "textAlign"]}
            initialValue="left"
          >
            <Radio.Group>
              <Radio.Button value="left">左</Radio.Button>
              <Radio.Button value="center">中</Radio.Button>
              <Radio.Button value="right">右</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            className="wbuc-mb-0 wbuc-flex-1"
            label="垂直对齐"
            name={["style", "verticalAlign"]}
            initialValue="middle"
          >
            <Radio.Group>
              <Radio.Button value="top">上</Radio.Button>
              <Radio.Button value="middle">中</Radio.Button>
              <Radio.Button value="bottom">下</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </div>
        {mode === "single" && (
          <>
            <DotLabel>单元格字段取值</DotLabel>
            <Form.Item name="type" noStyle>
              <Select
                placeholder="请选择字段类型"
                options={[
                  { label: "文本", value: DATA_TYPE.FORMAT },
                  {
                    label: "赋值",
                    value: DATA_TYPE.DYNAMIC,
                  },
                  // {
                  //   label: "自定义值",
                  //   value: DATA_TYPE.FORMULA,
                  // },
                ]}
                onChange={() => {
                  setFieldValue("value", []);
                }}
              />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => prev.type !== curr.type}
            >
              {({ getFieldValue }) => {
                const type = getFieldValue("type");
                if (
                  ![
                    DATA_TYPE.DYNAMIC,
                    DATA_TYPE.FORMULA,
                    DATA_TYPE.FORMAT,
                  ].includes(type)
                ) {
                  return null;
                }
                return (
                  <Form.Item name="value" noStyle>
                    <ValueEditor
                      type={type}
                      config={withFormulasFields}
                      onChange={(v) => {
                        // 设置默认舍入和小数位数
                        const rounding = getFieldValue(["attr", "rounding"]);
                        const precision = getFieldValue(["attr", "precision"]);
                        const newAttr = {
                          rounding,
                          precision,
                        };
                        if (type === DATA_TYPE.DYNAMIC) {
                          const [item] = v.filter(
                            (i) =>
                              isField(i.value, i.type) &&
                              flds.some((f) => f.code === i.value)
                          );
                          if (!item) {
                            return;
                          }
                          const field = flds.find((i) => i.code === item.value);
                          if (field.type === "number") {
                            newAttr.rounding = field?.roundMode ?? "round";
                            newAttr.precision = field?.decimalPlaces ?? 4;
                          }
                        }
                        form.setFieldsValue({ attr: newAttr });
                        collect();
                        setSelectionConfig({ attr: newAttr });
                      }}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => prev.value !== curr.value}
            >
              {({ getFieldValue }) => {
                const value = getFieldValue("value") ?? [];
                let dynamicSettingEle = null;
                if (value.some((i) => i.type === DATA_TYPE.DYNAMIC)) {
                  dynamicSettingEle = (
                    <div className="wbuc-flex wbuc-items-center wbuc-justify-between wbuc-gap-2">
                      <span>字段值为空时展示</span>
                      <Form.Item name={"placeholder"} noStyle initialValue="--">
                        <Input className="wbuc-flex-1" size="small" />
                      </Form.Item>
                    </div>
                  );
                }
                // 数字类型
                if (hasFieldType(VALUE_TYPE.NUMBER, value, flds)) {
                  dynamicSettingEle = (
                    <>
                      {dynamicSettingEle}
                      <DotLabel>数值设置</DotLabel>
                      <div className="wbuc-flex wbuc-items-center wbuc-justify-between wbuc-gap-2">
                        <span className="wbuc-flex-shrink-0">舍入方式</span>
                        <Form.Item
                          name={["attr", "rounding"]}
                          noStyle
                          initialValue="round"
                        >
                          <Select
                            options={[
                              { label: "四舍五入", value: "round" },
                              { label: "截断", value: "truncate" },
                            ]}
                            className="wbuc-flex-1"
                          />
                        </Form.Item>
                        <Form.Item
                          name={["attr", "thousandSeparator"]}
                          initialValue={false}
                          noStyle
                          valuePropName="checked"
                        >
                          <Checkbox className="wbuc-flex-shrink-0">
                            千分位
                          </Checkbox>
                        </Form.Item>
                      </div>
                      <div className="wbuc-flex wbuc-items-center wbuc-justify-between wbuc-gap-2">
                        <span className="wbuc-flex-shrink-0">保留</span>
                        <Form.Item
                          name={["attr", "precision"]}
                          noStyle
                          initialValue={4}
                        >
                          <Select
                            options={Array.from({ length: 10 }, (_, i) => ({
                              label: i,
                              value: i,
                            }))}
                            className="wbuc-flex-1"
                          />
                        </Form.Item>
                        <span className="wbuc-flex-shrink-0">位小数</span>
                        <Form.Item
                          name={["attr", "trailingZeroes"]}
                          initialValue={false}
                          noStyle
                          valuePropName="checked"
                        >
                          <Checkbox className="wbuc-flex-shrink-0">
                            补尾零
                          </Checkbox>
                        </Form.Item>
                      </div>
                    </>
                  );
                }
                if (hasFieldType(VALUE_TYPE.IMAGE, value, flds)) {
                  dynamicSettingEle = (
                    <>
                      {dynamicSettingEle}
                      <DotLabel>图片设置</DotLabel>
                      <Form.Item
                        name={["attr", "displayMode"]}
                        initialValue="floating"
                        noStyle
                      >
                        <Select
                          options={[
                            { label: "内嵌", value: "embedded" },
                            { label: "悬浮", value: "floating" },
                          ]}
                        />
                      </Form.Item>
                      <Form.Item
                        name={["attr", "width"]}
                        noStyle
                        initialValue="auto"
                      >
                        {renderImageSizeInput("width")}
                      </Form.Item>
                      <Form.Item
                        name={["attr", "height"]}
                        noStyle
                        initialValue="auto"
                      >
                        {renderImageSizeInput("height")}
                      </Form.Item>
                    </>
                  );
                }
                return dynamicSettingEle;
              }}
            </Form.Item>
          </>
        )}
      </Form>
    </div>
  );
};

export default Panel;
